import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface LeaderboardEntry {
  id: string;
  user_id: string;
  display_name: string;
  is_anonymous: boolean;
  messages_sent: number;
  replies_received: number;
  saves_by_others: number;
  shares: number;
  streak_days: number;
  total_score: number;
  current_rank: number | null;
  previous_rank: number | null;
  badge_key: string | null;
}

const BADGE_MAP: Record<string, { icon: string; label: string }> = {
  heart_starter: { icon: '❤️', label: 'Heart Starter' },
  consistent_voice: { icon: '🔥', label: 'Consistent Voice' },
  deep_impact: { icon: '💬', label: 'Deep Impact' },
  ripple_maker: { icon: '🌍', label: 'Ripple Maker' },
  legacy_builder: { icon: '🌳', label: 'Legacy Builder' },
};

export function getBadgeInfo(key: string | null) {
  if (!key) return BADGE_MAP.heart_starter;
  return BADGE_MAP[key] || BADGE_MAP.heart_starter;
}

export function getRankDescriptor(rank: number | null): string {
  if (!rank) return 'Just getting started';
  if (rank === 1) return 'Making the biggest impact';
  if (rank === 2) return 'An incredible voice';
  if (rank === 3) return 'Showing up for what matters';
  if (rank <= 10) return 'Making a real impact';
  return 'Every word counts';
}

export function useLeaderboard() {
  const { user } = useAuth();
  const [topEntries, setTopEntries] = useState<LeaderboardEntry[]>([]);
  const [userEntry, setUserEntry] = useState<LeaderboardEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      setIsLoading(true);

      // Rankings are maintained by trusted server-side publishing workflows.
      // Dashboard clients only read them; refresh functions are intentionally
      // unavailable to normal signed-in users.
      const { data: top } = await supabase
        .from('leaderboard_scores' as any)
        .select('*')
        .gt('total_score', 0)
        .order('total_score', { ascending: false })
        .limit(7);

      const entries = (top || [])
        .filter((e: any) => e.current_rank !== null) // Exclude admin users (no rank assigned)
        .map((e: any) => ({
          ...e,
          display_name: e.is_anonymous ? anonymize(e.display_name) : formatName(e.display_name),
        })) as LeaderboardEntry[];

      setTopEntries(entries);

      // Get current user's entry
      const { data: me } = await supabase
        .from('leaderboard_scores' as any)
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (me) {
        setUserEntry({
          ...(me as any),
          display_name: (me as any).is_anonymous ? anonymize((me as any).display_name) : formatName((me as any).display_name),
        });
      }

      setIsLoading(false);
    };

    load();
  }, [user?.id]);

  return { topEntries, userEntry, isLoading };
}

function formatName(name: string): string {
  if (!name || name === 'Anonymous') return 'Anonymous';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function anonymize(name: string): string {
  if (!name || name === 'Anonymous') return 'Someone who cares';
  return `${name[0]}***`;
}
